import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Download,
  Mail,
  Phone,
  Instagram,
  Globe,
  Calendar,
  Clock,
  MapPin,
  Ticket,
  Image as ImageIcon,
  FileText,
  Heart,
  Quote
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { eventInfo, images } from '../data/mock';

const DownloadCard = ({ title, desc, url, ext = 'PNG' }) => (
  <a
    href={url}
    download
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`press-download-${title.toLowerCase().replace(/\s+/g, '-')}`}
    className="group flex items-start gap-4 p-5 bg-white/5 hover:bg-white/[0.08] border border-white/10 hover:border-pink-500/40 rounded-2xl transition-all duration-300"
  >
    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/20 to-orange-500/20 border border-pink-500/30 flex items-center justify-center">
      <Download className="w-5 h-5 text-pink-400" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">
          {title}
        </h3>
        <span className="text-xs font-mono px-2 py-0.5 rounded bg-white/10 text-white/60">{ext}</span>
      </div>
      <p className="text-white/60 text-sm">{desc}</p>
    </div>
  </a>
);

const PressPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canonical = 'https://www.alohatequilafest.com/press';

  return (
    <div className="min-h-screen bg-gray-950" data-testid="press-page">
      <Helmet>
        <title>Press & Media Kit | Aloha Tequila & Arts Festival 2026</title>
        <meta
          name="description"
          content="Press kit for the Aloha Tequila & Arts Festival — Hawaii's premier tequila festival, June 20, 2026 at International Market Place, Waikiki. Download the press release, logos, and media assets."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Press & Media Kit | Aloha Tequila & Arts Festival 2026" />
        <meta
          property="og:description"
          content="Press release, logos, photos, and media contact for the Aloha Tequila & Arts Festival — June 20, 2026 in Waikiki."
        />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.alohatequilafest.com/' },
            { '@type': 'ListItem', position: 2, name: 'Press & Media Kit', item: canonical }
          ]
        })}</script>
      </Helmet>

      <Header />

      <main className="pt-28 sm:pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-4">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/30 mb-4">
              <FileText className="w-4 h-4 text-pink-400" />
              <span className="text-pink-300 text-sm font-medium tracking-wider uppercase">Press & Media Kit</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400 mb-4">
              Press & Media
            </h1>
            <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
              Everything journalists, influencers, and brand partners need to cover Aloha Tequila Fest 2026.
            </p>
          </div>

          {/* Fact sheet */}
          <div className="grid md:grid-cols-2 gap-6 mb-14">
            {/* Event quick facts */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8">
              <h2 className="text-xl font-bold text-white mb-4">Event at a glance</h2>
              <ul className="space-y-3 text-white/80 text-sm sm:text-base">
                <li className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Saturday, June 20, 2026</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span>5:00 PM – 9:00 PM</span>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-white">3 floors at International Market Place</strong><br />
                    2330 Kalakaua Ave, Honolulu, HI 96815
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Ticket className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                  <span>Tickets via TicketSpice · 21+ with valid ID</span>
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-pink-500 flex-shrink-0 mt-0.5" />
                  <span>Partial proceeds benefit <strong className="text-white">Aloha Cancer Project 501(c)(3)</strong></span>
                </li>
              </ul>
            </div>

            {/* Media contact */}
            <div className="bg-gradient-to-br from-pink-500/10 via-orange-500/10 to-yellow-500/5 border border-pink-500/40 rounded-3xl p-6 sm:p-8">
              <h2 className="text-xl font-bold text-white mb-4">Media contact</h2>
              <div className="space-y-3">
                <p className="text-white font-bold text-lg">Aloha Tequila Fest Team</p>
                <a
                  href="mailto:everything@8eight8.net"
                  data-testid="press-contact-email"
                  className="flex items-center gap-2 text-white/85 hover:text-pink-400 transition-colors"
                >
                  <Mail className="w-4 h-4 text-pink-400" />
                  everything@8eight8.net
                </a>
                <a
                  href="tel:+18083012134"
                  data-testid="press-contact-phone"
                  className="flex items-center gap-2 text-white/85 hover:text-pink-400 transition-colors"
                >
                  <Phone className="w-4 h-4 text-orange-400" />
                  808-301-2134
                </a>
                <a
                  href="https://www.instagram.com/alohatequilafest"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="press-contact-instagram"
                  className="flex items-center gap-2 text-white/85 hover:text-pink-400 transition-colors"
                >
                  <Instagram className="w-4 h-4 text-pink-400" />
                  @alohatequilafest
                </a>
                <a
                  href="https://www.alohatequilafest.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="press-contact-website"
                  className="flex items-center gap-2 text-white/85 hover:text-pink-400 transition-colors"
                >
                  <Globe className="w-4 h-4 text-yellow-400" />
                  www.alohatequilafest.com
                </a>
              </div>
              <p className="mt-5 pt-5 border-t border-white/10 text-white/70 text-sm leading-relaxed">
                Available for interviews, media credentials, high-res imagery, partnership opportunities, and influencer collaborations.
              </p>
            </div>
          </div>

          {/* Downloadable assets */}
          <section className="mb-14">
            <div className="flex items-center gap-2 mb-5">
              <ImageIcon className="w-5 h-5 text-pink-400" />
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Downloadable Assets</h2>
            </div>
            <p className="text-white/65 text-sm mb-6">
              Right-click or tap & hold any asset below to save. For additional formats (vector, transparent, print-ready), email everything@8eight8.net.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <DownloadCard
                title="Festival Logo"
                desc="Primary Aloha Tequila & Arts Festival logo"
                url={images.logo}
                ext="PNG"
              />
              <DownloadCard
                title="Festival Background / Hero"
                desc="Full-bleed hero image for features & social"
                url="https://customer-assets.emergentagent.com/job_oahu-tequilafest/artifacts/7fhvpnxc_Aloha%20Tequila%20Fest%20Background.png"
                ext="PNG"
              />
              <DownloadCard
                title="Aloha Cancer Project Logo"
                desc="Beneficiary 501(c)(3) logo"
                url={images.acpLogo}
                ext="JPG"
              />
              <DownloadCard
                title="Alt logo (secondary)"
                desc="Alternate logo variant for backgrounds"
                url="https://customer-assets.emergentagent.com/job_oahu-tequilafest/artifacts/bilzd50w_Aloha%20Tequila%20logo%202.png"
                ext="PNG"
              />
            </div>
          </section>

          {/* Pull quote */}
          <section className="mb-14">
            <div className="bg-gradient-to-br from-pink-500/10 to-orange-500/5 border-l-4 border-pink-500 rounded-r-2xl p-6 sm:p-8">
              <Quote className="w-8 h-8 text-pink-400 mb-3" />
              <p className="text-white text-lg sm:text-xl italic leading-relaxed mb-3">
                Aloha Tequila Fest is more than just a tasting event — it&apos;s a cultural and social experience that connects people through great energy, premium spirits, and the spirit of aloha. We&apos;re building something that Hawaii hasn&apos;t seen before at this level.
              </p>
              <p className="text-white/70 text-sm font-semibold">— Aloha Tequila Fest Organizers</p>
            </div>
          </section>

          {/* Full press release */}
          <section className="mb-14" data-testid="press-release-content">
            <div className="flex items-center gap-2 mb-5">
              <FileText className="w-5 h-5 text-pink-400" />
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Full Press Release</h2>
            </div>

            <article className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8 text-white/85 leading-relaxed space-y-5 text-base">
              <p className="text-white/60 uppercase tracking-widest text-xs font-bold">FOR IMMEDIATE RELEASE</p>

              <p className="text-lg sm:text-xl text-white font-semibold">
                Aloha Cancer Project 501(c)(3) is excited to launch Aloha Tequila Fest coming to Honolulu 6/20/2026 as Hawaii&apos;s Premier Tequila & Cultural Experience.
              </p>

              <p>
                <strong className="text-white">Honolulu, HI — 5/1/2025 —</strong> Aloha Tequila Fest, Hawaii&apos;s leading tequila and lifestyle festival, is set for our 1st annual Tequila Festival in Honolulu with an elevated experience that blends premium spirits, culinary excellence, live entertainment, and island culture into one unforgettable event. Portions of Aloha Tequila Festival will go to Aloha Cancer Project 501(c)(3) in loving memory of Christa Wittmier and Daniel Gray.
              </p>

              <p>
                Bringing together tequila enthusiasts, industry leaders, and experience seekers, Aloha Tequila Fest showcases a curated selection of world-class tequila brands, immersive activations, and a vibrant social atmosphere designed to celebrate both the spirit of tequila and the energy of Hawaii.
              </p>

              <div>
                <p className="font-semibold text-white mb-2">Guests can expect:</p>
                <ul className="space-y-1.5 list-none pl-0">
                  {[
                    'Tastings from top tequila brands and emerging labels',
                    'Exclusive VIP experiences',
                    'Live music, DJs, and cultural performances',
                    'Elevated food offerings from local chefs and vendors',
                    'Interactive brand activations and curated experiences'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="text-pink-400 flex-shrink-0">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <blockquote className="border-l-2 border-pink-500/60 pl-4 italic text-white/90">
                &quot;Aloha Tequila Fest is more than just a tasting event — it&apos;s a cultural and social experience that connects people through great energy, premium spirits, and the spirit of aloha,&quot; said the festival organizing team. &quot;We&apos;re building something that Hawaii hasn&apos;t seen before at this level.&quot;
              </blockquote>

              <p>
                As interest in tequila continues to surge globally, Aloha Tequila Fest positions Honolulu as a destination for high-end lifestyle events and world-class festivals. The event is expected to attract both local attendees and visitors, contributing to Hawaii&apos;s growing reputation as a hub for unique cultural and premium experiences.
              </p>

              <div>
                <p className="font-semibold text-white mb-2">Event Details:</p>
                <ul className="space-y-1.5 list-none pl-0">
                  <li className="flex gap-3">
                    <span className="text-pink-400 flex-shrink-0">•</span>
                    <span><strong className="text-white">Location:</strong> 3 Floors at International Market Place, Honolulu, HI</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-pink-400 flex-shrink-0">•</span>
                    <span><strong className="text-white">Date:</strong> 6/20/2026, 5pm–9pm</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-pink-400 flex-shrink-0">•</span>
                    <span><strong className="text-white">Tickets:</strong> Available now at www.alohatequilafest.com</span>
                  </li>
                </ul>
              </div>

              <p>Media, brand partners, and influencers are invited to participate and collaborate.</p>

              <div>
                <p className="font-semibold text-white mb-2">For press inquiries, partnerships, or media passes, please contact:</p>
                <p>
                  <strong className="text-white">Aloha Tequila Fest Press Team</strong><br />
                  everything@8eight8.net<br />
                  808-301-2134
                </p>
              </div>

              <p>
                <strong className="text-white">Follow along for updates and announcements:</strong><br />
                Instagram: @alohatequilafest<br />
                Tickets & inquiries: www.alohatequilafest.com
              </p>

              <hr className="border-white/10" />

              <div>
                <p className="font-semibold text-white mb-2">About Aloha Tequila Fest</p>
                <p>
                  Aloha Tequila Fest is Hawaii&apos;s premier tequila festival, bringing together premium brands, culinary experiences, and live entertainment in a vibrant, high-energy environment. The festival celebrates the culture of tequila while delivering a luxury event experience rooted in the spirit of aloha.
                </p>
              </div>

              <p className="text-white/50 text-center font-bold pt-4">###</p>
            </article>
          </section>

          {/* Quick copy-paste boilerplate */}
          <section className="mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5">Boilerplate copy</h2>
            <p className="text-white/65 text-sm mb-4">For quick inclusion in articles, event listings, and social posts.</p>

            <div className="space-y-4">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <p className="text-pink-400 text-xs uppercase tracking-wider font-semibold mb-2">Short (75 words)</p>
                <p className="text-white/80 text-sm leading-relaxed">
                  Aloha Tequila Fest debuts on Saturday, June 20, 2026 from 5–9 PM across 3 floors at International Market Place in Waikiki. Hawaii&apos;s premier tequila festival brings together world-class tequila brands, exclusive VIP experiences, elevated food from local chefs, and live music, DJs, and cultural performances. Partial proceeds benefit Aloha Cancer Project 501(c)(3) in loving memory of Christa Wittmier and Daniel Gray. 21+. Tickets at www.alohatequilafest.com.
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <p className="text-pink-400 text-xs uppercase tracking-wider font-semibold mb-2">One-liner (for event calendars)</p>
                <p className="text-white/80 text-sm leading-relaxed">
                  Hawaii&apos;s premier tequila festival — premium spirits, elevated food, live entertainment, and island culture across 3 floors at International Market Place. Benefits Aloha Cancer Project. 21+.
                </p>
              </div>
            </div>
          </section>

          {/* CTA back to home / tickets */}
          <div className="text-center">
            <Link
              to="/"
              data-testid="press-back-home-link"
              className="text-pink-400 hover:text-orange-400 transition-colors font-semibold text-sm"
            >
              ← Back to home
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PressPage;
